import React, { FC, useEffect, useState } from 'react';
import { useLazyGetAllCustomersQuery, useLazyGetMerchantsByTeamIdInvoiceQuery } from 'containers/invoices/api';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import { getTextForFilter as getTextForFilterUtil } from 'containers/dispatch/utils';
import { ESTIMATE_TAB_TITLE, EstimatesStatuses } from '../constants';
import CustomAmountFilter, { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import EstimateMoreFilter from './EstimateMoreFilter';
import { JobLeadSourceTypes } from 'containers/jobs/constant';
import { AmountFilterOperation } from 'constants/common';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';

interface EstimateFiltersProps {
  statuses: string[];
  setStatuses: (value: string[]) => void;
  currentTeamId: number;
  selectedCustomers: string[];
  setSelectedCustomers: (value: string[]) => void;
  selectedMerchants: string[];
  setSelectedMerchants: (value: string[]) => void;
  currentTab: ESTIMATE_TAB_TITLE;
  startDate: string | null;
  setStartDate: (startDate: string | null) => void;
  endDate: string | null;
  setEndDate: (endDate: string | null) => void;
  amountFilter: AmountFilter;
  setAmountFilter: (amountFilter: AmountFilter) => void;
  selectedLeadSource: string[];
  setSelectedLeadSource: (value: string[]) => void;
  selectedTeamMembers: string[];
  setSelectedTeamMembers: (value: string[]) => void;
  expiresInStartDate: string | null;
  setExpiresInStartDate: (startDate: string | null) => void;
  expiresInEndDate: string | null;
  setExpiresInEndDate: (endDate: string | null) => void;
}

const EstimateFilters: FC<EstimateFiltersProps> = ({
  statuses,
  setStatuses,
  currentTeamId,
  selectedCustomers,
  setSelectedCustomers,
  selectedMerchants,
  setSelectedMerchants,
  currentTab,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  amountFilter,
  setAmountFilter,
  selectedLeadSource,
  setSelectedLeadSource,
  selectedTeamMembers,
  setSelectedTeamMembers,
  expiresInStartDate,
  setExpiresInStartDate,
  expiresInEndDate,
  setExpiresInEndDate
}) => {
  const [getCustomersQuery, getCustomersQueryState] = useLazyGetAllCustomersQuery();
  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdInvoiceQuery();
  const [getAssignableTeamMemberQuery, getAssignableTeamMemberState] = useLazyGetAssignableMembersQuery();

  const { data: getCustomersResponse } = getCustomersQueryState;
  const { data: getMerchantsResponse } = getMerchantsQueryState;
  const { data: assignableTeamMembersData, isSuccess: assignableTeamMembersSuccess } = getAssignableTeamMemberState;

  const [statusesDraft, setStatusesDraft] = useState<string[]>([]);
  const [customerSearchString, setCustomerSearchString] = useState('');
  const [selectedCustomersDraft, setSelectedCustomersDraft] = useState<string[]>([]);
  const [createdBySearchString, setCreatedBySearchString] = useState('');
  const [selectedMerchantsDraft, setSelectedMerchantsDraft] = useState<string[]>([]);
  const [leadSourcesDraft, setLeadSourcesDraft] = useState<string[]>([]);
  const [assigneeSearchString, setAssigneeSearchString] = useState('');
  const [assigneesDraft, setAssigneesDraft] = useState<string[]>([]);
  const currentDate = new Date();
  const next3DaysDate = new Date(currentDate.getTime() + 3 * 24 * 60 * 60 * 1000);
  const thisWeekLastDate = new Date(new Date().setDate(currentDate.getDate() - currentDate.getDay() + 6));
  const thisMonthLastDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  useEffect(() => {
    if (currentTeamId) {
      getCustomersQuery({ teamId: currentTeamId });
      getMerchantsQuery({ teamId: currentTeamId });
      getAssignableTeamMemberQuery(currentTeamId.toString());
    }
  }, [currentTeamId, getCustomersQuery, getMerchantsQuery]);

  useEffect(() => {
    if (currentTab === ESTIMATE_TAB_TITLE.ALL_ESTIMATES) {
      setStatusesDraft(EstimatesStatuses.map(status => status.value));
    }
  }, [currentTab]);

  const getTextForFilter = (filter: string) => {
    switch (filter) {
      case 'statuses':
        return getTextForFilterUtil(EstimatesStatuses, statuses, 'value', 'label');
      case 'customers':
        return getTextForFilterUtil(getCustomersResponse?.customers || [], selectedCustomers, 'ID', 'name', 'lastName');
      case 'merchants':
        return getTextForFilterUtil(
          getMerchantsResponse || [],
          selectedMerchants,
          'merchantId',
          'firstName',
          'lastName'
        );
      case 'leadSource':
        return getTextForFilterUtil(JobLeadSourceTypes || [], selectedLeadSource, 'value', 'label');
      case 'soldBY':
        return getTextForFilterUtil(
          assignableTeamMembersData?.data?.teamMemberList || [],
          selectedTeamMembers,
          'merchantId',
          'name'
        );
      default:
        return '';
    }
  };

  const checkCustomFieldSelected = (range: { startDate: Date; endDate: Date }, filterEndDate: Date) => {
    return (
      currentDate.toDateString() === range.startDate.toDateString() &&
      filterEndDate.toDateString() === range.endDate.toDateString()
    );
  };

  const ExpiresOnDateStaticRanges = [
    {
      label: 'Today',
      isSelected: range => checkCustomFieldSelected(range, currentDate),
      range: () => ({
        startDate: currentDate,
        endDate: currentDate
      })
    },
    {
      label: 'Next 3 days',
      isSelected: range => checkCustomFieldSelected(range, next3DaysDate),
      range: () => ({
        startDate: currentDate,
        endDate: next3DaysDate
      })
    },
    {
      label: 'This Week',
      isSelected: range => checkCustomFieldSelected(range, thisWeekLastDate),
      range: () => ({
        startDate: currentDate,
        endDate: thisWeekLastDate
      })
    },
    {
      label: 'This Month',
      isSelected: range => checkCustomFieldSelected(range, thisMonthLastDate),
      range: () => ({
        startDate: currentDate,
        endDate: thisMonthLastDate
      })
    }
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {currentTab === ESTIMATE_TAB_TITLE.ALL_ESTIMATES && (
        <DropdownWithSearch
          filterId="estimates-status-filter"
          data={EstimatesStatuses}
          selectedDraftArray={statusesDraft}
          setSelectedDraftArray={setStatusesDraft}
          selectedArray={statuses}
          setSelectedArray={setStatuses}
          dataKey="label"
          dataIdKey="value"
          buttonPrimaryText="Status"
          buttonSecondaryText={getTextForFilter('statuses')}
          filterPlaceholderText="Filter by status"
          allowSearch={false}
          filterPlaceholderStyle="font-semibold"
          searchInputStyle="placeholder:text-primaryText"
        />
      )}
      <CustomDateRangePicker
        filterId="estimates-date-filter"
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        placeholder="Created on"
      />
      <DropdownWithSearch
        searchFilterId="estimates-createdby-search-field"
        filterId="estimates-createdby-filter"
        data={getMerchantsResponse || []}
        searchText={createdBySearchString}
        setSearchText={setCreatedBySearchString}
        selectedDraftArray={selectedMerchantsDraft}
        setSelectedDraftArray={setSelectedMerchantsDraft}
        selectedArray={selectedMerchants}
        setSelectedArray={setSelectedMerchants}
        dataKey="firstName"
        secondaryDataKey="lastName"
        dataIdKey={'merchantId'}
        buttonPrimaryText="Created by"
        buttonSecondaryText={getTextForFilter('merchants')}
        filterPlaceholderText="Filter by merchants"
        searchPlaceHolderText="Search merchant names"
        allowSearch={true}
        filterPlaceholderStyle="font-semibold"
        searchInputStyle="placeholder:text-primaryText"
      />
      <DropdownWithSearch
        searchFilterId="estimate-sold-by-members-search-field"
        filterId="estimate-sold-by-members-filter"
        data={(assignableTeamMembersSuccess && assignableTeamMembersData?.data?.teamMemberList) || []}
        searchText={assigneeSearchString}
        setSearchText={setAssigneeSearchString}
        selectedDraftArray={assigneesDraft}
        setSelectedDraftArray={setAssigneesDraft}
        selectedArray={selectedTeamMembers}
        setSelectedArray={setSelectedTeamMembers}
        dataKey="name"
        dataIdKey={'merchantId'}
        buttonPrimaryText="Sold by"
        buttonSecondaryText={getTextForFilter('soldBY')}
        filterPlaceholderText="Filter by sold by"
        searchPlaceHolderText="Search sold by members"
        allowSearch={true}
        filterPlaceholderStyle="font-semibold"
        searchInputStyle="placeholder:text-primaryText"
      />
      <DropdownWithSearch
        searchFilterId="estimates-customer-name-search-field"
        filterId="estimates-customer-filter"
        data={getCustomersResponse?.customers || []}
        searchText={customerSearchString}
        setSearchText={setCustomerSearchString}
        selectedDraftArray={selectedCustomersDraft}
        setSelectedDraftArray={setSelectedCustomersDraft}
        selectedArray={selectedCustomers}
        setSelectedArray={setSelectedCustomers}
        dataKey="name"
        secondaryDataKey="lastName"
        dataIdKey={'ID'}
        buttonPrimaryText="Customers"
        buttonSecondaryText={getTextForFilter('customers')}
        filterPlaceholderText="Filter by customers"
        searchPlaceHolderText="Search customer names"
        allowSearch={true}
        filterPlaceholderStyle="font-semibold"
        searchInputStyle="placeholder:text-primaryText"
      />
      <CustomDateRangePicker
        filterId="estimates-expires-on-filter"
        startDate={expiresInStartDate}
        setStartDate={setExpiresInStartDate}
        endDate={expiresInEndDate}
        setEndDate={setExpiresInEndDate}
        placeholder="Expires in"
        allowFutureDate={true}
        allowPastDate={false}
        hasDefaultRanges={false}
        customRanges={ExpiresOnDateStaticRanges}
      />
      {amountFilter.filterOperation !== AmountFilterOperation.ALL || selectedLeadSource.length > 0 ? (
        <>
          <DropdownWithSearch
            filterId="estimates-lead-source-filter"
            data={JobLeadSourceTypes || []}
            selectedDraftArray={leadSourcesDraft}
            setSelectedDraftArray={setLeadSourcesDraft}
            selectedArray={selectedLeadSource}
            setSelectedArray={setSelectedLeadSource}
            dataKey="label"
            dataIdKey="value"
            buttonPrimaryText="Lead source"
            buttonSecondaryText={getTextForFilter('leadSource')}
            filterPlaceholderText="Filter by lead source"
            allowSearch={false}
            filterPlaceholderStyle="font-semibold"
          />
          <CustomAmountFilter amountFilter={amountFilter} setAmountFilter={setAmountFilter} />
        </>
      ) : (
        <EstimateMoreFilter
          filterId="estimates-more-filter"
          data={JobLeadSourceTypes || []}
          selectedDraftArray={leadSourcesDraft}
          setSelectedDraftArray={setLeadSourcesDraft}
          setSelectedArray={setSelectedLeadSource}
          dataKey="label"
          dataIdKey="value"
          filterPlaceholderText="Filter by Lead source"
          allowSearch={false}
          filterPlaceholderStyle="font-semibold"
          amountFilter={amountFilter}
          setAmountFilter={setAmountFilter}
        />
      )}
    </div>
  );
};

export default EstimateFilters;
